import { convertImageUrlToEncodedUrl } from '@campsite-bio/component-lib';
import defaultOg from '~/assets/default-og';
import { profileFonts } from '@/utils/profile-fonts';
import { getMeta } from '@/utils/meta';
import { generateGtagScript, generateGTMScript } from '~/utils/analytics';
import {
  generateFacebookPixelScript,
  generatePinterestScript,
  generateTikTokScript,
} from '~/utils/pixels';

export function head() {
  let favicons = [];
  const preload = [];
  const canonical = [];
  let ogImage = defaultOg;
  if (this.hasCustomFavicon) {
    favicons = [
      {
        hid: 'appleicon',
        rel: 'apple-touch-icon-precomposed',
        href: convertImageUrlToEncodedUrl(
          this.campsite.custom_meta_favico.url,
          200,
        ),
      },
      {
        hid: 'favicon',
        rel: 'icon',
        href: convertImageUrlToEncodedUrl(
          this.campsite.custom_meta_favico.url,
          200,
        ),
      },
    ];
  }
  if (this.hasCustomOGImage) {
    ogImage = [
      {
        hid: 'image',
        name: 'image',
        property: 'og:image',
        content: this.campsite.custom_meta_ogimage.url,
      },
      {
        hid: 'twitter:image',
        name: 'twitter:image',
        content: this.campsite.custom_meta_ogimage.url,
      },
    ];
  }

  // Add canonical link to the head
  // Exclude if they have a custom domain
  if (this.isFreePlan || (!this.isFreePlan && !this.campsite.custom_domain)) {
    canonical.push({
      hid: 'canonical',
      rel: 'canonical',
      href: `https://campsite.bio/${this.campsite.username_lowercase}`,
    });
  }

  const {
    site_font_family_details,
    site_button_font_family_details,
    site_title_font_family_details,
    site_title_inherit,
    site_title_font_weight,
  } = this.campsite.theme ? this.campsite.theme : this.campsite;

  const fontWeight = this.campsite.theme
    ? this.campsite.theme.site_button_font_weight
    : this.campsite.site_button_font_weight;
  // <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,400;1,300;1,400&family=Roboto:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap" rel="stylesheet">
  const fonts = [];
  const fontFamilies = profileFonts(
    site_font_family_details,
    site_button_font_family_details,
    fontWeight,
    site_title_font_family_details,
    site_title_inherit,
    site_title_font_weight,
  );
  if (fontFamilies.length) {
    const href = `https://fonts.googleapis.com/css2?family=${fontFamilies.join(
      '&family=',
    )}&display=swap`;
    fonts.push({
      rel: 'preload',
      as: 'style',
      href,
      onload: "this.onload=null;this.rel='stylesheet'",
    });
  }

  let metaTitle = `${this.campsite.username}'s Campsite.bio`;
  if (!this.isFreePlan && this.campsite.custom_meta_title)
    metaTitle = this.campsite.custom_meta_title;

  let metaDescription = `${this.campsite.username}'s Campsite.bio profile. Create your own bio link for free today.`;
  if (!this.isFreePlan && this.campsite.custom_meta_description)
    metaDescription = this.campsite.custom_meta_description;

  const scripts = [];
  if (this.isIframe) {
    scripts.push({
      src: `${this.$config.cdnHost}/js/iframe-resizer@4.2.9/iframeResizer.contentWindow.min.js`,
      async: true,
    });
  }
  // User GTM tag
  const gtmId = getMeta('google_tag_manager_id', null, this.campsite.meta);
  if (!this.isFreePlan && gtmId) {
    scripts.push(generateGTMScript('gtm', gtmId));
  }
  // User adwords tag
  const adwordsId =
    this.campsite.site_google_ads_id ||
    getMeta('google_ads_id', null, this.campsite.meta);
  if (!this.isFreePlan && adwordsId) {
    scripts.push(...generateGtagScript('adwords', adwordsId));
  }
  // User facebook pixel
  const facebookPixelId =
    this.campsite.site_fb_pixel ||
    getMeta('fb_pixel_id', null, this.campsite.meta);
  if (!this.isFreePlan && facebookPixelId) {
    scripts.push(generateFacebookPixelScript('facebook', facebookPixelId));
  }
  // User GA tag
  const googleAnalyticsId =
    this.campsite.site_google_analytics_id ||
    getMeta('google_analytics_id', null, this.campsite.meta);
  if (!this.isFreePlan && googleAnalyticsId) {
    scripts.push(...generateGtagScript('googleanalytics', googleAnalyticsId));
  }
  // User Pinterest tag
  const pinterestTagId = getMeta('pinterest_tag_id', null, this.campsite.meta);
  if (!this.isFreePlan && pinterestTagId) {
    scripts.push(generatePinterestScript('pinterest', pinterestTagId));
  }
  // User TikTok tag
  const tiktokId = getMeta('tiktok_pixel_id', null, this.campsite.meta);
  if (!this.isFreePlan && tiktokId) {
    scripts.push(generateTikTokScript('tiktok', tiktokId));
  }

  let siteName = [];
  // Prevent Campsite.bio from showing up in rich previews
  // For Pro users who have hidden branding
  if (
    this.isFreePlan ||
    (!this.isFreePlan && !this.campsite.site_hide_branding)
  ) {
    siteName = [
      {
        property: 'og:site_name',
        content: 'Campsite.bio',
      },
      {
        name: 'twitter:domain',
        content: 'Campsite.bio',
      },
    ];
  }

  const links = this.$store.state.links
    .slice(0, 5)
    .filter(
      (l) =>
        l.image ||
        (l.custom_media && l.custom_media.length) ||
        (l.type === 'carousel' &&
          l.custom_banner_media &&
          l.custom_banner_media.length),
    );

  // preload top link images
  links.forEach((link) => {
    let image = { href: convertImageUrlToEncodedUrl(link.image, 150) };
    if (link.custom_media && link.custom_media.length)
      image.href = convertImageUrlToEncodedUrl(link.custom_media[0].url, 150);
    else if (link.type === 'carousel') {
      // Get first banners image
      const firstBanner = link.banners.filter((b) => b.enabled && b.media)[0];
      const mediaItem = link.custom_banner_media.find(
        (b) => b._id.$oid === firstBanner?.media.$oid,
      );

      if (mediaItem) {
        const imageSrc = convertImageUrlToEncodedUrl(mediaItem.url, 650);
        const imageHighResSrc = convertImageUrlToEncodedUrl(
          mediaItem.url,
          1300,
        );
        image.href = imageSrc;
        image.imagesrcset = `${imageSrc} 650w, ${imageHighResSrc} 1080w`;
        image.imagesizes = '(min-width: 600px) 600w, 100vw';
      }
    }
    preload.push({
      rel: 'preload',
      as: 'image',
      ...image,
    });
  });

  return {
    title: metaTitle,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: metaDescription,
      },
      ...ogImage,
      {
        property: 'og:type',
        content: 'profile',
      },
      {
        name: 'profile:username',
        content: this.campsite.username_lowercase,
      },
      ...siteName,
      {
        name: 'twitter:card',
        content: 'summary',
      },

      {
        name: 'twitter:title',
        content: metaTitle,
      },
      {
        name: 'twitter:description',
        content: metaDescription,
      },
    ],
    bodyAttrs: {
      class: this.bodyClasses.join(' '),
    },
    link: [...preload, ...favicons, ...fonts, ...canonical],
    script: [...scripts],
    __dangerouslyDisableSanitizers: ['style'],
    __dangerouslyDisableSanitizersByTagID: {
      gtm: ['innerHTML'],
      adwords: ['innerHTML'],
      facebook: ['innerHTML'],
      googleanalytics: ['innerHTML'],
      pinterest: ['innerHTML'],
      tiktok: ['innerHTML'],
    },
  };
}
